import { Button, Input } from "@cieda/djayma-ui";
import { X } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FilterOption {
  readonly value: string;
  readonly label: string;
}

export interface FilterConfig {
  readonly key: string;
  readonly label: string;
  readonly type: "select" | "date" | "status" | "search";
  readonly options?: readonly FilterOption[];
}

export interface FilterBarProps {
  readonly filters: readonly FilterConfig[];
  readonly values: Readonly<Record<string, unknown>>;
  readonly onChange: (key: string, value: unknown) => void;
}

// ---------------------------------------------------------------------------
// Individual filter renderers
// ---------------------------------------------------------------------------

function SelectFilter({
  config,
  value,
  onChange,
}: {
  config: FilterConfig;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
}) {
  return (
    <select
      value={String(value ?? "")}
      onChange={(e) => onChange(config.key, e.target.value || null)}
      className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
      aria-label={config.label}
    >
      <option value="">{config.label}</option>
      {config.options?.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function DateFilter({
  config,
  value,
  onChange,
}: {
  config: FilterConfig;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
}) {
  return (
    <Input
      type="date"
      value={String(value ?? "")}
      onChange={(e) => onChange(config.key, e.target.value || null)}
      className="h-9 w-auto text-sm"
      aria-label={config.label}
    />
  );
}

function SearchFilter({
  config,
  value,
  onChange,
}: {
  config: FilterConfig;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
}) {
  return (
    <Input
      type="text"
      placeholder={config.label}
      value={String(value ?? "")}
      onChange={(e) => onChange(config.key, e.target.value || null)}
      className="h-9 w-40 text-sm"
      aria-label={config.label}
    />
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FilterBar({ filters, values, onChange }: FilterBarProps) {
  const hasActiveFilters = Object.values(values).some(
    (v) => v != null && v !== "",
  );

  const handleReset = () => {
    filters.forEach((f) => onChange(f.key, null));
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {filters.map((config) => {
        const value = values[config.key];

        switch (config.type) {
          case "select":
          case "status":
            return (
              <SelectFilter
                key={config.key}
                config={config}
                value={value}
                onChange={onChange}
              />
            );
          case "date":
            return (
              <DateFilter
                key={config.key}
                config={config}
                value={value}
                onChange={onChange}
              />
            );
          case "search":
            return (
              <SearchFilter
                key={config.key}
                config={config}
                value={value}
                onChange={onChange}
              />
            );
          default:
            return null;
        }
      })}

      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={handleReset} className="gap-1 text-xs">
          <X className="h-3 w-3" />
          Réinitialiser
        </Button>
      )}
    </div>
  );
}
